<template>
  <div class="sa-shell">
    <!-- Sidebar -->
    <aside class="sa-sidebar">
      <div class="sa-brand">
        <v-icon color="white" size="26">mdi-shield-crown</v-icon>
        <div class="sa-brand-text">
          <span class="sa-brand-title">Super Admin</span>
          <span class="sa-brand-sub">Panou administrare</span>
        </div>
      </div>

      <nav class="sa-nav">
        <button
          v-for="s in sections"
          :key="s.key"
          class="sa-nav-item"
          :class="{ active: s.key === activeKey }"
          @click="activeKey = s.key"
        >
          <v-icon size="20">{{ s.icon }}</v-icon>
          <span>{{ s.text }}</span>
        </button>
      </nav>

      <div class="sa-side-footer">
        <span class="sa-ver">v{{ APP_Version }}</span>
        <span class="sa-copy">© 2026 Else Digital Solutions</span>
      </div>
    </aside>

    <!-- Main -->
    <div class="sa-main">
      <header class="sa-header">
        <div class="sa-head-title">
          <v-icon :icon="activeSection.icon" size="22" color="#ff2c21" />
          <span>{{ activeSection.text }}</span>
        </div>
        <div class="sa-head-actions">
          <span class="sa-chip"><v-icon size="14">mdi-shield-check</v-icon> Privilegii complete</span>
          <v-btn variant="tonal" class="sa-back" prepend-icon="mdi-arrow-left"
            :loading="switching" :disabled="switching" @click="backToApp">
            Înapoi la aplicație
          </v-btn>
          <v-btn variant="text" class="sa-logout" prepend-icon="mdi-logout"
            :disabled="switching" @click="logout">
            Deconectare
          </v-btn>
        </div>
      </header>

      <main class="sa-content">
        <div class="sa-card">
          <component :is="activeSection.comp" />
        </div>
      </main>
    </div>

    <!-- Overlay la comutarea înapoi în aplicație -->
    <div v-if="switching" class="sa-switch-overlay">
      <div class="sa-switch-box">
        <v-progress-circular indeterminate size="46" width="4" color="#ff2c21" />
        <span class="sa-switch-text">Se încarcă aplicația…</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import AppSettings from "@/settings/appSettings"
import AdminOverview from "./AdminOverview.vue"
import AdGridView from "./AdGridView.vue"
import AdSettings from "./AdSettings.vue"
import ERP_CompaniesGrid from "./ERP_CompaniesGrid.vue"
import UserAdminGrid from "./UserAdminGrid.vue"
import RoluriDrepturiGrid from "./RoluriDrepturiGrid.vue"
import RoluriDrepturiService from "@/services/AUTH/RoluriDrepturi"
import { clearGridComenziColumnFilters } from "@/utility/gridComenziState"
import BugReports from "./BugReports.vue"
import MaintenanceBroadcast from "./MaintenanceBroadcast.vue"
import BrandingGrid from "./BrandingGrid.vue"
import ImpersonateGrid from "./ImpersonateGrid.vue"

export default defineComponent({
  name: "SuperAdmin",
  components: { AdminOverview, AdGridView, AdSettings, ERP_CompaniesGrid, UserAdminGrid, RoluriDrepturiGrid, BugReports, MaintenanceBroadcast, BrandingGrid, ImpersonateGrid },

  data() {
    return {
      activeKey: "overview",
      switching: false,
      sections: [
        { key: "overview", text: "Prezentare generală", icon: "mdi-view-dashboard", comp: "AdminOverview" },
        { key: "users", text: "Utilizatori", icon: "mdi-account-group", comp: "AdGridView" },
        { key: "appusers", text: "Utilizatori aplicație", icon: "mdi-account-key", comp: "UserAdminGrid" },
        { key: "roluri", text: "Roluri & drepturi", icon: "mdi-shield-account", comp: "RoluriDrepturiGrid" },
        { key: "ad", text: "Configurare AD", icon: "mdi-cog", comp: "AdSettings" },
        { key: "firme", text: "Firme", icon: "mdi-domain", comp: "ERP_CompaniesGrid" },
        { key: "bugreports", text: "Rapoarte probleme", icon: "mdi-bug-outline", comp: "BugReports" },
        { key: "maintenance", text: "Notificare mentenanță", icon: "mdi-bullhorn", comp: "MaintenanceBroadcast" },
        { key: "branding", text: "Branding login", icon: "mdi-palette", comp: "BrandingGrid" },
        { key: "impersonate", text: "Impersonare", icon: "mdi-account-switch", comp: "ImpersonateGrid" },
      ],
      APP_Version: AppSettings.APP_Version,
    }
  },

  computed: {
    activeSection(): any {
      return this.sections.find(s => s.key === this.activeKey) || this.sections[0]
    },
  },

  methods: {
    async backToApp() {
      // La revenirea în aplicație, token-ul poate fi legat de altă firmă decât
      // cea din selector (secțiunea Roluri & drepturi apelează switch-company și
      // suprascrie ERA_Jwt fără să atingă ERA_Company). Re-sincronizăm token-ul
      // cu firma afișată în selector înainte de reload.
      this.switching = true
      try {
        const raw = localStorage.getItem("ERA_Company")
        const comp = raw ? JSON.parse(raw) : null
        if (comp?.ID) await RoluriDrepturiService.switchCompany(comp.ID)
      } catch { /* dacă eșuează, continuăm cu token-ul curent */ }
      localStorage.setItem("ERA_SuperAdminChoice", "false")
      this.$router.go(0)
    },
    logout() {
      localStorage.removeItem("ERA_Jwt")
      localStorage.removeItem("ERA_Company")
      localStorage.removeItem("ERA_SuperAdminChoice")
      clearGridComenziColumnFilters()
      this.$router.go(0)
    }
  }
})
</script>

<style scoped>
.sa-shell {
  display: flex;
  height: 100vh;
  background: #eef1f6;
  font-family: "Roboto", -apple-system, "Segoe UI", system-ui, sans-serif;
}

/* ===== Sidebar ===== */
.sa-sidebar {
  width: 256px;
  flex-shrink: 0;
  background: linear-gradient(180deg, #ff2c21 0%, #c1170f 100%);
  color: #fff;
  display: flex;
  flex-direction: column;
  padding: 18px 14px;
}

.sa-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 8px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
}

.sa-brand-text { display: flex; flex-direction: column; line-height: 1.15; }
.sa-brand-title { font-size: 16px; font-weight: 800; }
.sa-brand-sub { font-size: 11px; color: rgba(255, 255, 255, 0.75); }

.sa-nav { display: flex; flex-direction: column; gap: 4px; margin-top: 16px; flex: 1; }

.sa-nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 500;
  padding: 11px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.sa-nav-item:hover { background: rgba(255, 255, 255, 0.12); }

.sa-nav-item.active {
  background: #ffffff;
  color: #ff2c21;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.sa-side-footer { display: flex; flex-direction: column; gap: 2px; padding: 12px 10px 4px; border-top: 1px solid rgba(255, 255, 255, 0.15); }
.sa-ver { font-size: 12px; font-weight: 700; }
.sa-copy { font-size: 10px; color: rgba(255, 255, 255, 0.65); }

/* ===== Main ===== */
.sa-main { flex: 1; display: flex; flex-direction: column; min-width: 0; }

.sa-header {
  height: 64px;
  flex-shrink: 0;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 22px;
}

.sa-head-title { display: flex; align-items: center; gap: 10px; font-size: 17px; font-weight: 700; color: #1e293b; }

.sa-head-actions { display: flex; align-items: center; gap: 10px; }

.sa-chip {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  color: #059669;
  background: #ecfdf5;
  padding: 4px 12px;
  border-radius: 20px;
}

.sa-back {
  text-transform: none !important;
  font-weight: 600 !important;
  color: #ff2c21 !important;
  background: rgba(255, 44, 33, 0.1) !important;
}

.sa-logout {
  text-transform: none !important;
  font-weight: 500 !important;
  color: #64748b !important;
}

.sa-content {
  flex: 1;
  overflow: auto;
  padding: 22px;
}

.sa-card {
  background: #fff;
  border: 1px solid #eef1f6;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  padding: 20px;
  min-height: calc(100vh - 64px - 44px);
}

/* ===== Overlay comutare ===== */
.sa-switch-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.sa-switch-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  background: #fff;
  padding: 30px 40px;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
}

.sa-switch-text { font-size: 15px; font-weight: 600; color: #1e293b; }

/* ===== Responsive ===== */
@media (max-width: 860px) {
  .sa-sidebar { width: 64px; padding: 14px 8px; }
  .sa-brand-text, .sa-nav-item span, .sa-side-footer { display: none; }
  .sa-nav-item { justify-content: center; }
  .sa-chip { display: none; }
}
</style>

<!-- stil GLOBAL doar pentru popup-urile de editare din SuperAdmin (randate in overlay) -->
<style>
.era-edit-popup .dx-popup-title {
  background: linear-gradient(90deg, #ff2c21, #ff6a4a);
  border-bottom: none;
}
.era-edit-popup .dx-popup-title .dx-toolbar-label,
.era-edit-popup .dx-popup-title .dx-toolbar-label * {
  color: #ffffff !important;
  font-weight: 700;
}
.era-edit-popup .dx-popup-title .dx-closebutton .dx-icon {
  color: #ffffff !important;
}
.era-edit-popup .dx-popup-content { padding-top: 16px; }
.era-edit-popup .dx-popup-bottom .dx-button-mode-contained.dx-button-default {
  background: linear-gradient(90deg, #ff2c21, #ff6a4a);
  border: none;
}
.era-edit-popup .dx-popup-bottom .dx-button-mode-contained.dx-button-default .dx-button-text {
  color: #ffffff;
}
.era-edit-popup .dx-texteditor.dx-state-focused.dx-editor-outlined {
  border-color: #ff6a4a;
}
</style>
